<script setup>
/* global axios */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { THEMES } from '../gamification/gameThemes';
import GameCharacter from '../gamification/GameCharacter.vue';
import { useGameAppearance } from '../gamification/useGameAppearance';
import { HOME_SECONDARY_LINK_CLASS } from './homeTones';

const props = defineProps({
  userId: { type: [String, Number], default: null },
});
const { t } = useI18n();
const route = useRoute();
const appearance = useGameAppearance(
  () => route.params.accountId,
  () => props.userId
);
const data = ref(null);
const loading = ref(true);
const failed = ref(false);
let requestVersion = 0;
const theme = computed(
  () => THEMES[data.value?.season.theme] || THEMES.professional
);
const playful = computed(
  () => data.value && data.value.season.theme !== 'professional'
);
const completed = computed(
  () =>
    data.value?.missions.filter(m => m.claim?.status === 'approved').length || 0
);
// Posição real no ranking por XP e medalhas calculadas pelo backend.
const rankPosition = computed(
  () => data.value?.ranking?.me?.xp_position || null
);
const medals = computed(() => data.value?.achievements || []);
const earnedMedals = computed(() => medals.value.filter(m => m.earned).length);
const bandStyle = computed(() =>
  playful.value
    ? {
        background: `linear-gradient(120deg, ${theme.value.sky}, ${theme.value.ground})`,
      }
    : undefined
);
// Cor explícita no título: branco nos cenários escuros, semântica no profissional.
const titleStyle = computed(() => ({
  color: playful.value ? '#fff' : 'rgb(var(--slate-12))',
}));
const stats = computed(() =>
  data.value
    ? [
        {
          key: 'level',
          label: t('OPERATIONAL_HOME.CONQUESTS.LEVEL'),
          value: data.value.player.level,
        },
        {
          key: 'xp',
          label: t('OPERATIONAL_HOME.CONQUESTS.XP'),
          value: data.value.player.xp,
        },
        {
          key: 'points',
          label: t('OPERATIONAL_HOME.CONQUESTS.POINTS'),
          value: data.value.player.balance,
        },
      ]
    : []
);
const load = async () => {
  requestVersion += 1;
  const version = requestVersion;
  data.value = null;
  failed.value = false;
  loading.value = true;
  const base = `/api/v1/accounts/${route.params.accountId}/talkvia_game_seasons`;
  try {
    const response = await axios.get(base, { params: { view: 'participant' } });
    if (version !== requestVersion) return;
    const now = Date.now();
    const season = response.data.seasons.find(
      item =>
        item.status === 'active' &&
        new Date(item.starts_at) <= now &&
        new Date(item.ends_at) >= now
    );
    if (season) {
      const detail = await axios.get(`${base}/${season.id}`);
      if (version === requestVersion && detail.data.can_participate)
        data.value = detail.data;
    }
  } catch {
    if (version === requestVersion) failed.value = true;
  } finally {
    if (version === requestVersion) loading.value = false;
  }
};
watch(() => route.params.accountId, load, { immediate: true });
</script>

<template>
  <section
    class="conquests-home flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-sm dark:border-n-strong"
    :aria-label="t('OPERATIONAL_HOME.CONQUESTS.ARIA_LABEL')"
  >
    <div
      class="flex items-center gap-3 px-5 py-4"
      :class="
        playful ? 'text-white' : 'border-b border-n-weak dark:border-n-strong'
      "
      :style="bandStyle"
    >
      <GameCharacter
        v-if="playful"
        :key="`${route.params.accountId}:${data.season.id}`"
        :xp="data.player.xp"
        :character="appearance.character"
        :motion="appearance.motion"
        :accessory="appearance.accessory"
        compact
      />
      <span
        v-else
        class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-amber-3 text-n-amber-11"
        aria-hidden="true"
      >
        <span class="i-hugeicons-champion size-5" />
      </span>
      <div class="min-w-0">
        <span
          class="block text-[11px] font-semibold uppercase tracking-wider"
          :class="playful ? 'text-white' : 'text-n-slate-11'"
        >
          {{ t('OPERATIONAL_HOME.CONQUESTS.EYEBROW') }}
        </span>
        <h2
          class="m-0 mt-0.5 truncate text-base font-semibold"
          :style="titleStyle"
        >
          {{ data?.season.name || t('OPERATIONAL_HOME.CONQUESTS.EMPTY_TITLE') }}
        </h2>
      </div>
    </div>

    <div class="flex flex-1 flex-col gap-4 p-5">
      <p
        v-if="loading"
        class="m-0 text-sm text-n-slate-11 motion-safe:animate-pulse"
      >
        {{ t('OPERATIONAL_HOME.CONQUESTS.LOADING') }}
      </p>
      <p v-else-if="failed" class="m-0 text-sm text-n-slate-11">
        {{ t('OPERATIONAL_HOME.CONQUESTS.ERROR') }}
      </p>
      <template v-else-if="data">
        <dl class="m-0 grid grid-cols-3 gap-2">
          <div
            v-for="stat in stats"
            :key="stat.key"
            class="rounded-lg bg-n-solid-2 px-3 py-2"
          >
            <dt class="text-xs text-n-slate-11">{{ stat.label }}</dt>
            <dd class="m-0 text-lg font-semibold tabular-nums text-n-slate-12">
              {{ stat.value }}
            </dd>
          </div>
        </dl>
        <div>
          <div
            class="mb-1.5 flex flex-wrap justify-between gap-2 text-xs text-n-slate-11"
          >
            <span>{{ theme.name }}</span>
            <span>
              {{
                t('OPERATIONAL_HOME.CONQUESTS.GOALS_PROGRESS', {
                  done: completed,
                  total: data.missions.length,
                })
              }}
            </span>
          </div>
          <progress
            :value="completed"
            :max="Math.max(data.missions.length, 1)"
            :aria-label="t('OPERATIONAL_HOME.CONQUESTS.GOALS_ARIA')"
            class="block h-1.5 w-full overflow-hidden rounded-full text-n-brand"
          />
        </div>
        <div
          v-if="rankPosition || medals.length"
          class="flex flex-wrap gap-2 text-xs text-n-slate-11"
          data-conquests-highlights="true"
        >
          <span
            v-if="rankPosition"
            class="inline-flex items-center gap-1 rounded-full bg-n-solid-2 px-2 py-1"
          >
            <span class="i-hugeicons-ranking size-3.5 text-n-amber-11" />
            {{
              t('OPERATIONAL_HOME.CONQUESTS.RANK', { position: rankPosition })
            }}
          </span>
          <span
            v-if="medals.length"
            class="inline-flex items-center gap-1 rounded-full bg-n-solid-2 px-2 py-1"
          >
            <span class="i-hugeicons-award-01 size-3.5 text-n-amber-11" />
            {{
              t('OPERATIONAL_HOME.CONQUESTS.MEDALS', {
                count: earnedMedals,
                total: medals.length,
              })
            }}
          </span>
        </div>
      </template>
      <p v-else class="m-0 text-sm text-n-slate-11">
        {{ t('OPERATIONAL_HOME.CONQUESTS.EMPTY_DESCRIPTION') }}
      </p>

      <div class="mt-auto flex flex-wrap items-center gap-3">
        <RouterLink
          :to="{
            name: 'talkvia_gamification',
            params: { accountId: route.params.accountId },
            query: data ? { season: data.season.id } : {},
          }"
          :class="HOME_SECONDARY_LINK_CLASS"
        >
          <span class="i-hugeicons-champion size-4 text-n-brand" />
          {{
            data
              ? t('OPERATIONAL_HOME.CONQUESTS.CONTINUE')
              : t('OPERATIONAL_HOME.CONQUESTS.VIEW')
          }}
        </RouterLink>
        <button
          v-if="failed"
          type="button"
          class="text-sm font-medium text-n-brand underline-offset-2 hover:underline"
          @click="load"
        >
          {{ t('OPERATIONAL_HOME.CONQUESTS.RETRY') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
progress {
  accent-color: currentColor;
  appearance: none;
  border: 0;
}
progress::-webkit-progress-bar {
  background: rgb(var(--slate-4));
  border-radius: 8px;
}
progress::-webkit-progress-value {
  background: currentColor;
  border-radius: 8px;
}
progress::-moz-progress-bar {
  background: currentColor;
  border-radius: 8px;
}
</style>
